import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useVenue } from "../lib/venue";
import { getRecords, getRecordOrderReceipt } from "../lib/api";
import { PageSkeleton } from "../components/skeleton";
import { useCart, addToCart, removeFromCart, clearCart } from "../lib/cart";
import { isNativeApp } from "../lib/platform";
import { useWishlist } from "../lib/wishlist";
import { usePurchaseEvent, pushVirtualPageView } from "../lib/analytics";
import { playYoutubeAudio, stopYoutubeAudio } from "../lib/youtube-audio";
import type { RecordPublic } from "../lib/types";

const gbp = (cents: number) => `£${(cents / 100).toFixed(2)}`;
const PAGE_SIZE = 24;

type Sort = "featured" | "newest" | "price_asc" | "price_desc" | "artist";

// A synthetic path for the storefront's local-state-only filters/pagination
// (genre tiles, era/sort selects, "next page"), the real browser URL never
// changes for these, so this is what tells GA4 apart one browse view from
// another. Only non-default values are included, so "the crate" with
// nothing selected stays the plain "/" (matching what a real navigation
// there would report).
function syntheticRecordsPath(f: { genre: string | null; era: string | null; q: string; sort: Sort; page: number }): string {
  const params = new URLSearchParams();
  if (f.genre) params.set("genre", f.genre);
  if (f.era) params.set("era", f.era);
  if (f.q.trim()) params.set("q", f.q.trim());
  if (f.sort !== "featured") params.set("sort", f.sort);
  if (f.page > 0) params.set("page", String(f.page + 1)); // 1-indexed for readability in GA4 reports
  const qs = params.toString();
  return qs ? `/?${qs}` : "/";
}

// The RECORD_SHOP storefront. Renders as a website (hero, genre tiles, wide
// paginated grid) in a browser and as the app (compact) in the native shell.
export function RecordsStorefront() {
  const { data } = useVenue();
  const venueId = data?.venue.id;
  const venueName = data?.venue.appName || data?.venue.name || "Records";
  const web = !isNativeApp();
  // Admin-editable web styling (Storefront settings → appConfig.recordShop).
  const rs = data?.venue.appConfig?.recordShop;
  const showGenreTiles = rs?.showGenreTiles !== false;

  const [genre, setGenre] = useState<string | null>(null);
  const [era, setEra] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<Sort>("featured");
  const [page, setPage] = useState(0);
  const [playing, setPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Website nav (genre links / search) drives filters via URL search params.
  // That navigation is a real router navigation, so it already fires its own
  // virtual_page_view (see main.tsx's initPageViewTracking) with the real
  // URL, skipNextView suppresses the filter-effect below from firing a
  // second, synthetic one for the resulting genre/q state change.
  const skipNextView = useRef(false);
  const search = useRouterState({ select: (s) => s.location.search }) as { genre?: string; q?: string };
  useEffect(() => {
    if (search.genre !== undefined || search.q !== undefined) skipNextView.current = true;
    if (search.genre !== undefined) setGenre(search.genre || null);
    if (search.q !== undefined) setQ(search.q || "");
  }, [search.genre, search.q]);

  // Any filter change resets to the first page, and, since these are local-
  // state filters with no URL change of their own (genre tiles, era/sort
  // selects), fires a synthetic virtual_page_view so GA4 sees the browse
  // interaction. Pagination is tracked separately, directly from goToPage.
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    setPage(0);
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    if (skipNextView.current) {
      skipNextView.current = false;
      return;
    }
    pushVirtualPageView(syntheticRecordsPath({ genre, era, q, sort, page: 0 }));
  }, [genre, era, q, sort]);

  // Returning from a successful Stripe checkout, clear the bag + confirm.
  // ?checkout=<orderId> (was the literal string "success" before order.id
  // started riding in success_url, see records/checkout.ts), captured into
  // state before the URL is stripped, so usePurchaseEvent below still has it
  // on later renders.
  const [justPaid, setJustPaid] = useState(false);
  const [purchasedOrderId, setPurchasedOrderId] = useState<string | null>(null);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const orderId = p.get("checkout");
    if (orderId) {
      clearCart();
      setJustPaid(true);
      setPurchasedOrderId(orderId);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  usePurchaseEvent(purchasedOrderId, getRecordOrderReceipt);

  const query = useQuery({
    queryKey: ["records", venueId, genre, era, q, sort, page],
    queryFn: () =>
      getRecords(venueId!, {
        genre: genre ?? undefined,
        era: era ?? undefined,
        q: q.trim() || undefined,
        sort,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    enabled: !!venueId,
    placeholderData: keepPreviousData,
  });

  const wishlist = useWishlist(venueId);
  const facets = query.data?.facets;
  const items = query.data?.items ?? [];
  const staffPicks = query.data?.staffPicks ?? [];
  const onSale = query.data?.onSale ?? [];
  // Only feature the curated rows on the unfiltered landing view.
  const showSections = !genre && !era && !q.trim();
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const topGenres = useMemo(() => (facets?.genres ?? []).slice(0, web ? 8 : 12), [facets, web]);
  const eras = facets?.eras ?? [];

  function play(rec: RecordPublic) {
    if (playing === rec.stockId) return;
    // Prefer a direct mp3 preview; otherwise play the Discogs YouTube AUDIO
    // (hidden player, no video) starting at the "good bit". Same UX either way.
    if (rec.audioUrl) {
      const el = audioRef.current;
      if (!el) return;
      stopYoutubeAudio();
      el.src = rec.audioUrl;
      el.play().then(() => setPlaying(rec.stockId)).catch(() => setPlaying(null));
    } else if (rec.previewYoutubeId) {
      audioRef.current?.pause();
      setPlaying(rec.stockId);
      playYoutubeAudio(rec.previewYoutubeId, () => setPlaying(null), rec.previewStartSeconds).catch(() => setPlaying(null));
    }
  }
  function stop(rec: RecordPublic) {
    if (playing !== rec.stockId) return;
    audioRef.current?.pause();
    stopYoutubeAudio();
    setPlaying(null);
  }
  function togglePlay(rec: RecordPublic) {
    if (playing === rec.stockId) stop(rec);
    else play(rec);
  }

  function goToPage(p: number) {
    const next = Math.max(0, Math.min(totalPages - 1, p));
    setPage(next);
    window.scrollTo({ top: web ? 0 : 0, behavior: "smooth" });
    if (next !== page) {
      pushVirtualPageView(syntheticRecordsPath({ genre, era, q, sort, page: next }));
    }
  }

  const activeFilters = [
    genre && { label: genre, clear: () => setGenre(null) },
    era && { label: era, clear: () => setEra(null) },
    q.trim() && { label: `“${q.trim()}”`, clear: () => setQ("") },
  ].filter(Boolean) as { label: string; clear: () => void }[];

  if (!venueId) return <PageSkeleton rows={6} />;

  return (
    <div className={web ? "space-y-10" : "space-y-5"}>
      <audio ref={audioRef} onEnded={() => setPlaying(null)} className="hidden" />

      {/* Hero (web) / header (app) */}
      {web ? (
        <section className="relative -mt-8 flex min-h-[46vh] items-center justify-center overflow-hidden">
          {data?.venue.bannerUrl && (
            <img src={data.venue.bannerUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--background)]/40 via-[var(--background)]/30 to-[var(--background)]" />
          <div className="relative px-4 py-16 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.35em] text-[var(--accent)]">Independent record shop</p>
            <h1 className="text-4xl font-black uppercase leading-[0.95] tracking-tight text-[var(--foreground)] sm:text-6xl md:text-7xl">{rs?.heroHeadline || venueName}</h1>
            <p className="mx-auto mt-4 max-w-xl text-sm text-[var(--subtle)] sm:text-base">
              {rs?.heroSubhead || data?.venue.appDescription || "Changing the way you dig, rare, second-hand & new vinyl, hand-picked and audio-previewed."}
            </p>
            <a href="#crate" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3 text-sm font-bold transition hover:opacity-90" style={{ color: "var(--accent-fg)" }}>
              {rs?.heroCtaLabel || "Start digging"}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </a>
          </div>
        </section>
      ) : (
        <section className="-mx-4 -mt-6 px-4 pt-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-[var(--foreground)]">{venueName}</h1>
              <p className="mt-1 text-sm text-[var(--subtle)]">{total > 0 ? `${total} records in the crate` : "Crate-dig the collection"}</p>
            </div>
            <Link to="/cart" className="relative mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--border-default)] bg-[var(--surface)] text-[var(--foreground)] transition hover:border-[var(--border-strong)]" aria-label="View bag">
              <BagGlyph />
              <CartBadge />
            </Link>
          </div>
        </section>
      )}

      {justPaid && (
        <div className="rounded-[var(--radius)] border border-[var(--accent)] bg-[var(--accent)]/10 p-4 text-sm text-[var(--foreground)]">
          Order confirmed, thanks! A receipt is on its way to your email.
        </div>
      )}

      {/* Curated sections, only on the unfiltered landing view. */}
      {showSections && staffPicks.length > 0 && (
        <FeaturedRow
          title="Staff picks"
          records={staffPicks}
          playing={playing}
          onToggle={togglePlay}
          onHoverStart={play}
          onHoverEnd={stop}
          wishlist={wishlist}
        />
      )}
      {showSections && onSale.length > 0 && (
        <FeaturedRow
          title="On sale"
          records={onSale}
          playing={playing}
          onToggle={togglePlay}
          onHoverStart={play}
          onHoverEnd={stop}
          wishlist={wishlist}
        />
      )}

      {/* Browse by genre (web) */}
      {web && showGenreTiles && topGenres.length > 0 && (
        <section>
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[var(--muted)]">Browse by genre</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {topGenres.map((g) => {
              const active = genre === g.value;
              return (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setGenre(active ? null : g.value)}
                  className={`group relative flex aspect-[2/1] flex-col justify-end overflow-hidden rounded-[var(--radius)] border p-4 text-left transition ${
                    active ? "border-[var(--accent)] bg-[var(--accent)]/20" : "border-[var(--border-default)] bg-[var(--accent)]/[0.06] hover:bg-[var(--accent)]/15"
                  }`}
                >
                  <span className="text-lg font-bold leading-tight text-[var(--foreground)]">{g.value}</span>
                  <span className="text-xs text-[var(--muted)]">{g.count} records</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Filter / sort bar */}
      <section id="crate" className="scroll-mt-24 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-bold text-[var(--foreground)]">
            {genre || (q.trim() ? "Search" : "The crate")}
            <span className="ml-2 text-sm font-normal text-[var(--muted)]">{total} records</span>
          </h2>
          <div className="ml-auto flex items-center gap-2">
            {!web && (
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="min-w-0 flex-1 rounded-full border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm text-[var(--foreground)] placeholder:text-[var(--muted)] focus:border-[var(--accent)] focus:outline-none"
              />
            )}
            {eras.length > 0 && (
              <select
                value={era ?? ""}
                onChange={(e) => setEra(e.target.value || null)}
                className="rounded-full border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none"
              >
                <option value="">All eras</option>
                {eras.map((e) => <option key={e.value} value={e.value}>{e.value} ({e.count})</option>)}
              </select>
            )}
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
              className="rounded-full border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none"
            >
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="price_asc">Price ↑</option>
              <option value="price_desc">Price ↓</option>
              <option value="artist">Artist A–Z</option>
            </select>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {activeFilters.map((f, i) => (
              <button key={i} type="button" onClick={f.clear} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--accent)] bg-[var(--accent)]/10 px-3 py-1 text-xs text-[var(--foreground)]">
                {f.label}
                <span aria-hidden className="text-[var(--muted)]">✕</span>
              </button>
            ))}
            <button type="button" onClick={() => { setGenre(null); setEra(null); setQ(""); }} className="text-xs text-[var(--muted)] underline underline-offset-2 hover:text-[var(--foreground)]">Clear all</button>
          </div>
        )}

        {/* Grid */}
        {query.isLoading ? (
          <PageSkeleton rows={6} />
        ) : items.length === 0 ? (
          <div className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-10 text-center text-sm text-[var(--muted)]">
            No records match your filters.
          </div>
        ) : (
          <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${web ? "lg:grid-cols-4 xl:grid-cols-5" : ""} ${query.isPlaceholderData ? "opacity-60" : ""} transition-opacity`}>
            {items.map((rec) => (
              <RecordCard
                key={rec.id}
                rec={rec}
                playing={playing === rec.stockId}
                onToggle={() => togglePlay(rec)}
                onHoverStart={() => play(rec)}
                onHoverEnd={() => stop(rec)}
                wishlist={wishlist}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} onGo={goToPage} />
        )}
        {total > 0 && (
          <p className="text-center text-xs text-[var(--muted)]">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </p>
        )}
      </section>
    </div>
  );
}

// ── Record card (Yoyaku-style: cover + hover/click play + quick-add + meta) ──
// Hover-capable devices auto-preview on hover (with a short intent delay);
// touch devices use the always-visible play button. Both funnel through the
// parent's single audio element so only one preview plays at a time.
const CAN_HOVER =
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(hover: hover)").matches;

type WishlistCtl = ReturnType<typeof useWishlist>;

function RecordCard({
  rec, playing, onToggle, onHoverStart, onHoverEnd, wishlist,
}: {
  rec: RecordPublic;
  playing: boolean;
  onToggle: () => void;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  wishlist: WishlistCtl;
}) {
  const cart = useCart();
  const inBag = cart.some((i) => i.stockId === rec.stockId);
  const hoverTimer = useRef<number | undefined>(undefined);
  const saved = wishlist.savedIds.has(rec.id);
  const effectivePrice = rec.onSale && rec.salePriceCents != null ? rec.salePriceCents : rec.priceCents;

  const canPlay = !!(rec.audioUrl || rec.previewYoutubeId);
  const hoverProps =
    CAN_HOVER && canPlay
      ? {
          onMouseEnter: () => {
            hoverTimer.current = window.setTimeout(onHoverStart, 180);
          },
          onMouseLeave: () => {
            window.clearTimeout(hoverTimer.current);
            onHoverEnd();
          },
        }
      : {};

  return (
    <div {...hoverProps} className="group flex flex-col overflow-hidden rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] transition hover:border-[var(--border-strong)]">
      <div className="relative aspect-square overflow-hidden bg-[var(--surface-2)]">
        <Link to="/records/$stockId" params={{ stockId: rec.stockId }} className="block h-full w-full">
          {rec.imageUrl ? (
            <img src={rec.imageUrl} alt={`${rec.artist}, ${rec.title}`} loading="lazy" className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[var(--muted)]"><VinylGlyph /></div>
          )}
        </Link>
        {/* Badges (top-left) */}
        <div className="pointer-events-none absolute left-2 top-2 flex flex-col gap-1">
          {rec.onSale && (
            <span className="rounded-full bg-[var(--accent)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--accent-fg)" }}>Sale</span>
          )}
          {rec.staffPick && (
            <span className="rounded-full bg-[var(--foreground)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--background)]">Staff pick</span>
          )}
        </div>
        {/* Wishlist heart (top-right), only for signed-in account holders. */}
        {wishlist.enabled && (
          <button
            type="button"
            onClick={() => wishlist.toggle(rec.id)}
            aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
            title={saved ? "Saved" : "Save"}
            style={{ borderRadius: "9999px", border: "none", padding: 0, background: "rgba(255,255,255,0.96)", color: saved ? "var(--accent)" : "#3f3f46" }}
            className="absolute right-2 top-2 flex h-10 w-10 items-center justify-center shadow-lg ring-1 ring-black/5 transition hover:scale-110"
          >
            {saved ? (
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0z" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} className="h-6 w-6"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" /></svg>
            )}
          </button>
        )}
        {canPlay && (
          <>
            {/* Scrim behind the play button, on hover, or while playing. */}
            <div className={`pointer-events-none absolute inset-0 bg-black/30 transition-opacity duration-200 ${playing ? "opacity-100" : CAN_HOVER ? "opacity-0 group-hover:opacity-100" : "opacity-0"}`} />
            {/* Prominent centered play/pause button, revealed on hover (always
                shown on touch devices + while playing). */}
            <button
              type="button"
              onClick={onToggle}
              aria-label={playing ? "Pause preview" : "Play preview"}
              style={{ borderRadius: "9999px", border: "none", padding: 0, background: "var(--accent)", color: "var(--accent-fg)" }}
              className={`absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center shadow-xl transition duration-200 hover:scale-105 ${playing ? "scale-100 opacity-100 ring-2 ring-white/70" : CAN_HOVER ? "scale-90 opacity-0 group-hover:scale-100 group-hover:opacity-100" : "opacity-100"}`}
            >
              {playing ? <PauseGlyphLg /> : <PlayGlyphLg />}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() =>
            inBag
              ? removeFromCart(rec.stockId)
              : addToCart({ stockId: rec.stockId, artist: rec.artist, title: rec.title, priceCents: effectivePrice, imageUrl: rec.imageUrl })
          }
          aria-label={inBag ? "Remove from bag" : "Add to bag"}
          style={{
            borderRadius: "9999px", border: "none", padding: 0,
            background: inBag ? "var(--accent)" : "rgba(255,255,255,0.96)",
            color: inBag ? "var(--accent-fg)" : "#171717",
          }}
          className="absolute bottom-2 right-2 flex h-11 w-11 items-center justify-center shadow-lg ring-1 ring-black/5 transition hover:scale-110 md:opacity-0 md:group-hover:opacity-100"
          title={inBag ? "In your bag, click to remove" : "Add to bag"}
        >
          {inBag ? <CheckGlyph /> : <PlusGlyph />}
        </button>
      </div>
      <Link to="/records/$stockId" params={{ stockId: rec.stockId }} className="flex flex-1 flex-col gap-0.5 p-3">
        <p className="truncate text-sm font-semibold text-[var(--foreground)]" title={rec.artist}>{rec.artist}</p>
        <p className="truncate text-xs text-[var(--subtle)]" title={rec.title}>{rec.title}</p>
        {rec.label && <p className="truncate text-[11px] text-[var(--muted)]" title={rec.label}>{rec.label}</p>}
        <div className="mt-1.5 flex items-center justify-between">
          <span className="flex items-baseline gap-1.5">
            <span className="text-sm font-bold text-[var(--foreground)]">{gbp(effectivePrice)}</span>
            {rec.onSale && <span className="text-xs text-[var(--muted)] line-through">{gbp(rec.priceCents)}</span>}
          </span>
          {rec.condition && <span className="rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] text-[var(--muted)]">{rec.condition}</span>}
        </div>
      </Link>
    </div>
  );
}

// ── Curated horizontal section (Staff picks / On sale) ──────────────────────
function FeaturedRow({
  title, records, playing, onToggle, onHoverStart, onHoverEnd, wishlist,
}: {
  title: string;
  records: RecordPublic[];
  playing: string | null;
  onToggle: (r: RecordPublic) => void;
  onHoverStart: (r: RecordPublic) => void;
  onHoverEnd: (r: RecordPublic) => void;
  wishlist: WishlistCtl;
}) {
  return (
    <section>
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[var(--muted)]">{title}</h2>
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2 scrollbar-hide">
        {records.map((rec) => (
          <div key={rec.id} className="w-40 shrink-0 sm:w-44">
            <RecordCard
              rec={rec}
              playing={playing === rec.stockId}
              onToggle={() => onToggle(rec)}
              onHoverStart={() => onHoverStart(rec)}
              onHoverEnd={() => onHoverEnd(rec)}
              wishlist={wishlist}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Pagination (Prev · numbers with ellipsis · Next) ────────────────────────
function Pagination({ page, totalPages, onGo }: { page: number; totalPages: number; onGo: (p: number) => void }) {
  const cur = page + 1; // 1-indexed for display
  const nums: (number | "…")[] = [];
  const push = (n: number | "…") => nums.push(n);
  const spread = 1;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= cur - spread && i <= cur + spread)) push(i);
    else if (nums[nums.length - 1] !== "…") push("…");
  }
  const btn = "flex h-9 min-w-9 items-center justify-center rounded-[var(--radius)] border px-2 text-sm transition";
  return (
    <nav className="flex flex-wrap items-center justify-center gap-1.5 pt-2" aria-label="Pagination">
      <button type="button" onClick={() => onGo(page - 1)} disabled={page === 0} className={`${btn} border-[var(--border-default)] text-[var(--foreground)] hover:border-[var(--border-strong)] disabled:opacity-40`}>Prev</button>
      {nums.map((n, i) =>
        n === "…" ? (
          <span key={`e${i}`} className="px-1 text-[var(--muted)]">…</span>
        ) : (
          <button
            key={n}
            type="button"
            onClick={() => onGo(n - 1)}
            aria-current={n === cur ? "page" : undefined}
            className={`${btn} ${n === cur ? "border-[var(--accent)] bg-[var(--accent)] font-bold text-[var(--accent-fg)]" : "border-[var(--border-default)] text-[var(--foreground)] hover:border-[var(--border-strong)]"}`}
          >
            {n}
          </button>
        ),
      )}
      <button type="button" onClick={() => onGo(page + 1)} disabled={page >= totalPages - 1} className={`${btn} border-[var(--border-default)] text-[var(--foreground)] hover:border-[var(--border-strong)] disabled:opacity-40`}>Next</button>
    </nav>
  );
}

function CartBadge() {
  const cart = useCart();
  if (cart.length === 0) return null;
  return <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-bold" style={{ color: "var(--accent-fg)" }}>{cart.length}</span>;
}

const PlayGlyphLg = () => <svg viewBox="0 0 24 24" fill="currentColor" className="ml-0.5 h-8 w-8"><path d="M8 5v14l11-7z" /></svg>;
const PauseGlyphLg = () => <svg viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>;
const PlusGlyph = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} className="h-6 w-6"><path strokeLinecap="round" d="M12 5v14M5 12h14" /></svg>;
const CheckGlyph = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} className="h-6 w-6"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>;
const BagGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
  </svg>
);
const VinylGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4} className="h-10 w-10"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="2.5" /></svg>
);
